import type { ReactNode } from 'react';
import { AppDetailLayout } from '@/shared/app-detail-layout';
import { ErrorState } from '@/shared/feedback';
import { useAsyncResource } from '@/shared/hooks/use-async-resource';
import { aiApi } from '@/modules/ai';
import { taxonomyApi } from '@/modules/catalog/services/taxonomy-api';
import { SetupView } from '@/modules/catalog/pages/setup-page/parts/setup-view';
import { useSetup } from '@/modules/catalog/pages/setup-page/parts/use-setup';

/**
 * CATALOGUE SETUP — the assistant-assisted way to start a catalogue.
 *
 * ---------------------------------------------------------------------------
 * THE JOURNEY
 * ---------------------------------------------------------------------------
 * A merchant with an empty catalogue describes what they sell. The assistant
 * proposes a category tree and the attributes that go with it. The merchant
 * READS the proposal, edits what they want, accepts what they want, and saves
 * it. The save goes through the ordinary catalogue API, which revalidates
 * everything, because the catalogue may have changed between the proposal and
 * the save.
 *
 * ---------------------------------------------------------------------------
 * THE ASSISTANT NEVER WRITES
 * ---------------------------------------------------------------------------
 * Every write on this page is a catalogue API write, made by the console, one
 * item at a time. The assistant's proposal is a PROPOSAL: it names fields, and
 * it never carries a price, a stock level, a certification, a country or a
 * currency — there is no member for any of them.
 *
 * ---------------------------------------------------------------------------
 * AI IS AN AID, NEVER A REQUIREMENT
 * ---------------------------------------------------------------------------
 * When AI is not ready — off, unconfigured, unavailable or over its limit —
 * the page says so in plain words and the MANUAL forms still create
 * everything the assistant would have proposed.
 */
export function SetupPage(): ReactNode {
  const categories = useAsyncResource(loadCategories);
  const dictionary = useAsyncResource(loadDictionary);
  const ai = useAsyncResource(loadAiSettings);

  const aiReady = ai.data !== null && ai.data.availability === 'ready';
  const setup = useSetup({
    categories: categories.data ?? [],
    dictionary: dictionary.data ?? [],
    aiReady,
    onCategoriesChanged: categories.reload,
  });

  if (categories.isLoading) {
    return <AppDetailLayout title="Catalogue setup" sections={[]} isLoading />;
  }

  if (categories.error !== null) {
    return (
      <AppDetailLayout
        title="Catalogue setup"
        sections={[]}
        toolbar={
          <ErrorState
            title="Could not load your catalogue"
            message={categories.error.message}
            onRetry={categories.reload}
          />
        }
      />
    );
  }

  return (
    <SetupView
      setup={setup}
      ai={ai}
      categories={categories}
      dictionaryError={dictionary.error === null ? null : dictionary.error.message}
      onRetryDictionary={dictionary.reload}
    />
  );
}

/** The tenant's category tree. */
async function loadCategories() {
  return taxonomyApi.listCategories();
}

/** The tenant's attribute dictionary. */
async function loadDictionary() {
  return taxonomyApi.listAttributeDictionary();
}

/** The tenant's effective AI settings — whether the assistant can work. */
async function loadAiSettings() {
  return aiApi.getEffectiveSettings();
}
